import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faInfoCircle,
    faCheckCircle,
    faTimes,
    faSync,
    faBoxOpen,
    faBox,
    faUserCircle,
    faExclamationTriangle,
    faChevronDown,
    faChevronUp,
    faTrash,
} from '@fortawesome/free-solid-svg-icons';

const SetStatusModal = ({ isOpen, onClose, set, onUpdateQuantity, onUpdateComplete, onUpdateSealed, setSets, onRemove }) => {
    const { user } = useAuth();
    const [quantity, setQuantity] = useState(set ? Number(set.quantity) : 0);
    const [completeCount, setCompleteCount] = useState(set ? Number(set.complete) : 0);
    const [sealedCount, setSealedCount] = useState(set ? Number(set.sealed) : 0);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [removeConfirmPending, setRemoveConfirmPending] = useState(false);
    const [error, setError] = useState('');

    const [minifigs, setMinifigs] = useState([]);
    const [loadingMinifigs, setLoadingMinifigs] = useState(false);
    const [showMinifigs, setShowMinifigs] = useState(false);
    const [updatingMinifigs, setUpdatingMinifigs] = useState({});

    useEffect(() => {
        if (set) {
            setQuantity(Number(set.quantity));
            setCompleteCount(Number(set.complete));
            setSealedCount(Number(set.sealed));
            setError('');
            setRemoveConfirmPending(false);
            fetchMinifigures();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [set]);

    const fetchMinifigures = async () => {
        if (!set) return [];
        setLoadingMinifigs(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/get_set_minifigures.php?set_num=${set.set_num}`,
                { withCredentials: true }
            );
            if (response.data.error) {
                setMinifigs([]);
                return [];
            }
            const minifigData = response.data.minifigs || [];
            setMinifigs(minifigData);
            return minifigData;
        } catch {
            setMinifigs([]);
            return [];
        } finally {
            setLoadingMinifigs(false);
        }
    };

    const updateMinifigQuantity = async (figNum, newQuantity) => {
        setUpdatingMinifigs(prev => ({ ...prev, [figNum]: true }));
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/update_collection_minifigure.php`,
                {
                    user_id: Number(user.user_id),
                    set_num: set.set_num,
                    fig_num: figNum,
                    quantity_owned: newQuantity,
                },
                { withCredentials: true }
            );
            if (response.data.success) {
                setMinifigs(prev => prev.map(m =>
                    m.fig_num === figNum ? { ...m, owned_quantity: newQuantity } : m
                ));
                updateSetMinifigureCounts(set.set_num);
            } else {
                setError('Failed to update minifigure quantity');
            }
        } catch {
            setError('Failed to update minifigure quantity');
        }
        setUpdatingMinifigs(prev => ({ ...prev, [figNum]: false }));
    };

    const updateSetMinifigureCounts = (setNum) => {
        setSets(prevSets => prevSets.map(s => {
            if (s.set_num !== setNum) return s;
            const totalOwned = minifigs.reduce((sum, m) => sum + (m.owned_quantity || 0), 0);
            const totalExpected = minifigs.reduce((sum, m) => sum + (m.required_quantity * Number(s.quantity)), 0);
            return {
                ...s,
                owned_minifigures: totalOwned.toString(),
                expected_minifigures: totalExpected.toString(),
            };
        }));
    };

    if (!isOpen || !set) return null;

    const validateAndUpdate = () => {
        if (completeCount > quantity) {
            setError('Complete count cannot exceed total quantity');
            return;
        }
        if (sealedCount > quantity) {
            setError('Sealed count cannot exceed total quantity');
            return;
        }

        let adjustedCompleteCount = completeCount;
        if (sealedCount > completeCount) {
            adjustedCompleteCount = sealedCount;
            setCompleteCount(sealedCount);
        }

        setIsUpdating(true);
        setError('');

        const originalQuantity = Number(set.quantity);
        const quantityChanged = originalQuantity !== quantity;
        const updatedSet = {
            ...set,
            quantity: quantity.toString(),
            complete: adjustedCompleteCount.toString(),
            sealed: sealedCount.toString(),
        };

        onUpdateQuantity(set.set_num, quantity)
            .then(() => onUpdateComplete(set.set_num, adjustedCompleteCount))
            .then(() => onUpdateSealed(set.set_num, sealedCount))
            .then(() => {
                setSets(prevSets => prevSets.map(s =>
                    s.set_num === set.set_num ? updatedSet : s
                ));
                if (quantityChanged) {
                    fetchMinifigures().then(newMinifigData => {
                        if (newMinifigData && newMinifigData.length > 0) {
                            const totalOwned = newMinifigData.reduce((sum, m) => sum + (m.owned_quantity || 0), 0);
                            const totalExpected = newMinifigData.reduce((sum, m) => sum + (m.required_quantity * quantity), 0);
                            setSets(prevSets => prevSets.map(s =>
                                s.set_num === set.set_num ? {
                                    ...s,
                                    owned_minifigures: totalOwned.toString(),
                                    expected_minifigures: totalExpected.toString(),
                                } : s
                            ));
                        }
                    });
                }
                setIsUpdating(false);
                onClose();
            })
            .catch(err => {
                setError('Failed to update. Please try again.');
                setIsUpdating(false);
                console.error('Error updating set status:', err);
            });
    };

    const increaseQuantity = () => {
        setQuantity(prev => prev + 1);
        setCompleteCount(prev => prev + 1);
    };

    const decreaseQuantity = () => {
        if (quantity <= 1) return;
        const newQty = quantity - 1;
        setQuantity(newQty);
        if (completeCount > newQty) setCompleteCount(newQty);
        if (sealedCount > newQty) setSealedCount(newQty);
    };

    const increaseComplete = () => {
        if (completeCount >= quantity) return;
        setCompleteCount(prev => prev + 1);
    };

    const decreaseComplete = () => {
        if (completeCount <= 0) return;
        const newCount = completeCount - 1;
        setCompleteCount(newCount);
        if (sealedCount > newCount) setSealedCount(newCount);
    };

    const increaseSealed = () => {
        if (sealedCount >= quantity) return;
        const newCount = sealedCount + 1;
        setSealedCount(newCount);
        if (newCount > completeCount) setCompleteCount(newCount);
    };

    const decreaseSealed = () => {
        if (sealedCount <= 0) return;
        setSealedCount(prev => prev - 1);
    };

    // Derived values used in the minifigure section
    const minifigCompleteCount = minifigs.filter(m =>
        (m.owned_quantity || 0) >= (m.required_quantity * quantity)
    ).length;
    const minifigTotalCount = minifigs.length;
    const minifigProgressPct = minifigTotalCount > 0 ? (minifigCompleteCount / minifigTotalCount) * 100 : 0;
    const hasAnyMinifigOwned = minifigs.some(m => (m.owned_quantity || 0) > 0);
    const allMinifigsComplete = minifigCompleteCount === minifigTotalCount && minifigTotalCount > 0;
    const anyMinifigUpdating = Object.values(updatingMinifigs).some(Boolean);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 backdrop-filter backdrop-blur-sm p-2 sm:p-4" onClick={onClose}>
            <div className="bg-white rounded-xl sm:rounded-2xl max-w-5xl w-full max-h-[95vh] sm:max-h-[90vh] flex flex-col shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200 p-4 sm:p-6 flex-shrink-0 rounded-t-xl sm:rounded-t-2xl">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-100 rounded-lg sm:rounded-xl overflow-hidden shadow-sm border border-slate-200 flex-shrink-0">
                                <img
                                    src={set.img_url}
                                    alt={set.name}
                                    className="w-full h-full object-contain"
                                    onError={e => { e.target.src = '/images/lego_piece_questionmark.png'; }}
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h2 className="text-lg sm:text-2xl font-bold text-slate-800 mb-1">Update Set Status</h2>
                                <h3 className="text-sm sm:text-lg font-semibold text-slate-700 mb-1 truncate">{set.name}</h3>
                                <p className="text-xs sm:text-sm text-slate-500 font-mono">Set #{set.set_num}</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-800 transition-all duration-200 flex items-center justify-center flex-shrink-0 ml-2"
                        >
                            <FontAwesomeIcon icon={faTimes} className="text-sm sm:text-lg" />
                        </button>
                    </div>
                </div>

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto min-h-0">
                    <div className="p-4 sm:p-6 space-y-6 sm:space-y-8">
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3">
                                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 text-sm" />
                                </div>
                                <span className="text-red-800 font-medium">{error}</span>
                            </div>
                        )}

                        {/* Status controls */}
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                {/* Quantity */}
                                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                                    <div className="text-center mb-4">
                                        <div className="w-8 h-8 bg-slate-600 rounded-full flex items-center justify-center mx-auto mb-2">
                                            <FontAwesomeIcon icon={faBoxOpen} className="text-white text-sm" />
                                        </div>
                                        <span className="text-sm font-semibold text-slate-700 block">Quantity</span>
                                        <span className="text-xs text-slate-500">Total sets owned</span>
                                    </div>
                                    <div className="flex items-center justify-center gap-3">
                                        <button
                                            className="w-10 h-10 bg-slate-600 hover:bg-slate-700 disabled:bg-slate-300 text-white rounded-xl transition-all duration-200 flex items-center justify-center font-semibold disabled:cursor-not-allowed"
                                            onClick={decreaseQuantity}
                                            disabled={quantity <= 1}
                                        >−</button>
                                        <span className="text-2xl font-bold text-slate-800 w-16 text-center">{quantity}</span>
                                        <button
                                            className="w-10 h-10 bg-slate-600 hover:bg-slate-700 text-white rounded-xl transition-all duration-200 flex items-center justify-center font-semibold"
                                            onClick={increaseQuantity}
                                        >+</button>
                                    </div>
                                </div>

                                {/* Complete */}
                                <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                                    <div className="text-center mb-4">
                                        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-2">
                                            <FontAwesomeIcon icon={faCheckCircle} className="text-white text-sm" />
                                        </div>
                                        <span className="text-sm font-semibold text-blue-800 block">Complete</span>
                                        <span className="text-xs text-blue-600 font-medium">{completeCount} of {quantity} sets</span>
                                    </div>
                                    <div className="flex items-center justify-center gap-3 mb-3">
                                        <button
                                            className="w-10 h-10 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-xl transition-all duration-200 flex items-center justify-center font-semibold disabled:cursor-not-allowed"
                                            onClick={decreaseComplete}
                                            disabled={completeCount <= 0}
                                        >−</button>
                                        <span className="text-2xl font-bold text-blue-800 w-16 text-center">{completeCount}</span>
                                        <button
                                            className="w-10 h-10 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-xl transition-all duration-200 flex items-center justify-center font-semibold disabled:cursor-not-allowed"
                                            onClick={increaseComplete}
                                            disabled={completeCount >= quantity}
                                        >+</button>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            className="text-xs bg-white hover:bg-blue-100 border border-blue-200 py-2 px-3 rounded-lg transition-all duration-200 flex-1 font-medium text-blue-700 disabled:opacity-50"
                                            onClick={() => setCompleteCount(0)}
                                            disabled={completeCount === 0}
                                        >None</button>
                                        <button
                                            className="text-xs bg-white hover:bg-blue-100 border border-blue-200 py-2 px-3 rounded-lg transition-all duration-200 flex-1 font-medium text-blue-700 disabled:opacity-50"
                                            onClick={() => setCompleteCount(quantity)}
                                            disabled={completeCount === quantity}
                                        >All</button>
                                    </div>
                                </div>

                                {/* Sealed */}
                                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                                    <div className="text-center mb-4">
                                        <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-2">
                                            <FontAwesomeIcon icon={faBox} className="text-white text-sm" />
                                        </div>
                                        <span className="text-sm font-semibold text-emerald-800 block">Sealed</span>
                                        <span className="text-xs text-emerald-600 font-medium">{sealedCount} of {quantity} sets</span>
                                    </div>
                                    <div className="flex items-center justify-center gap-3 mb-3">
                                        <button
                                            className="w-10 h-10 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white rounded-xl transition-all duration-200 flex items-center justify-center font-semibold disabled:cursor-not-allowed"
                                            onClick={decreaseSealed}
                                            disabled={sealedCount <= 0}
                                        >−</button>
                                        <span className="text-2xl font-bold text-emerald-800 w-16 text-center">{sealedCount}</span>
                                        <button
                                            className="w-10 h-10 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white rounded-xl transition-all duration-200 flex items-center justify-center font-semibold disabled:cursor-not-allowed"
                                            onClick={increaseSealed}
                                            disabled={sealedCount >= quantity}
                                        >+</button>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            className="text-xs bg-white hover:bg-emerald-100 border border-emerald-200 py-2 px-3 rounded-lg transition-all duration-200 flex-1 font-medium text-emerald-700 disabled:opacity-50"
                                            onClick={() => setSealedCount(0)}
                                            disabled={sealedCount === 0}
                                        >None</button>
                                        <button
                                            className="text-xs bg-white hover:bg-emerald-100 border border-emerald-200 py-2 px-3 rounded-lg transition-all duration-200 flex-1 font-medium text-emerald-700 disabled:opacity-50"
                                            onClick={() => { setSealedCount(quantity); setCompleteCount(quantity); }}
                                            disabled={sealedCount === quantity}
                                        >All</button>
                                    </div>
                                </div>
                            </div>

                            {/* Info note */}
                            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                                <div className="flex items-center justify-center text-sm text-slate-600">
                                    <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-slate-500" />
                                    <span className="text-center">
                                        <strong>Complete:</strong> All pieces &amp; instructions present • <strong>Sealed:</strong> Unopened original packaging
                                        {sealedCount > completeCount && (
                                            <span className="block mt-1 text-amber-600 font-medium">
                                                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                                                Sealed sets will automatically be marked as complete
                                            </span>
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Minifigures section */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-slate-800 flex items-center">
                                <FontAwesomeIcon icon={faUserCircle} className="mr-2 text-slate-600" />
                                Minifigures
                                {minifigs.length > 0 && (
                                    <span className="ml-3 text-sm bg-purple-100 text-purple-700 px-3 py-1 rounded-full font-medium">
                                        {minifigs.length} figures
                                    </span>
                                )}
                            </h3>

                            {loadingMinifigs ? (
                                <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
                                    <FontAwesomeIcon icon={faSync} className="animate-spin text-slate-400 text-2xl mb-3" />
                                    <p className="text-slate-600 font-medium">Loading minifigures...</p>
                                </div>
                            ) : minifigs.length > 0 ? (
                                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 sm:p-6 space-y-4">
                                    <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-6">
                                        {/* Progress bar */}
                                        <div className="flex-1">
                                            <div className="bg-white rounded-lg px-4 sm:px-6 py-4 border border-purple-200">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center gap-2">
                                                        <FontAwesomeIcon icon={faUserCircle} className="text-purple-600" />
                                                        <span className="text-base sm:text-lg font-semibold text-slate-800">Collection Progress</span>
                                                    </div>
                                                    <span className="text-base sm:text-lg font-bold text-slate-800">
                                                        {minifigCompleteCount}/{minifigTotalCount}
                                                    </span>
                                                </div>
                                                <div className="relative">
                                                    <div className="w-full bg-slate-200 rounded-full h-3">
                                                        <div
                                                            className={`h-3 rounded-full transition-all duration-500 ${
                                                                allMinifigsComplete
                                                                    ? 'bg-emerald-500'
                                                                    : minifigCompleteCount > 0
                                                                        ? 'bg-blue-500'
                                                                        : 'bg-slate-300'
                                                            }`}
                                                            style={{ width: `${Math.max(minifigProgressPct, 0)}%` }}
                                                        />
                                                    </div>
                                                    <div className="mt-2 flex justify-between text-xs sm:text-sm">
                                                        <span className="text-slate-600">
                                                            {allMinifigsComplete
                                                                ? 'All minifigures complete!'
                                                                : minifigCompleteCount === 0
                                                                    ? 'No minifigures owned'
                                                                    : `${minifigTotalCount - minifigCompleteCount} still needed`
                                                            }
                                                        </span>
                                                        <span className="text-slate-600 font-medium">{Math.round(minifigProgressPct)}%</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Bulk action buttons */}
                                        <div className="flex flex-col gap-3 lg:w-48 lg:flex-shrink-0">
                                            <div className="flex flex-col sm:flex-row lg:flex-col gap-2 sm:gap-3 lg:gap-3">
                                                {!allMinifigsComplete && (
                                                    <button
                                                        onClick={async () => {
                                                            await Promise.all(minifigs.map(m =>
                                                                updateMinifigQuantity(m.fig_num, m.required_quantity * quantity)
                                                            ));
                                                        }}
                                                        className="flex-1 lg:flex-none px-3 sm:px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg transition-all duration-200 font-medium disabled:bg-emerald-300 text-center whitespace-nowrap"
                                                        disabled={anyMinifigUpdating}
                                                    >
                                                        <FontAwesomeIcon icon={faCheckCircle} className="mr-1 sm:mr-2" />
                                                        <span className="hidden sm:inline">Mark All Complete</span>
                                                        <span className="sm:hidden">All Complete</span>
                                                    </button>
                                                )}
                                                {hasAnyMinifigOwned && (
                                                    <button
                                                        onClick={async () => {
                                                            await Promise.all(minifigs.map(m =>
                                                                updateMinifigQuantity(m.fig_num, 0)
                                                            ));
                                                        }}
                                                        className="flex-1 lg:flex-none px-3 sm:px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white text-sm rounded-lg transition-all duration-200 font-medium disabled:bg-slate-300 text-center whitespace-nowrap"
                                                        disabled={anyMinifigUpdating}
                                                    >
                                                        <FontAwesomeIcon icon={faTimes} className="mr-1 sm:mr-2" />
                                                        <span className="hidden sm:inline">Mark All Missing</span>
                                                        <span className="sm:hidden">All Missing</span>
                                                    </button>
                                                )}
                                            </div>

                                            <button
                                                onClick={() => setShowMinifigs(v => !v)}
                                                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-all duration-200 shadow-sm text-sm whitespace-nowrap"
                                            >
                                                <FontAwesomeIcon icon={faUserCircle} />
                                                <span>{showMinifigs ? 'Hide Minifigures' : 'Show Minifigures'}</span>
                                                <FontAwesomeIcon icon={showMinifigs ? faChevronUp : faChevronDown} />
                                            </button>
                                        </div>
                                    </div>

                                    {showMinifigs && (
                                        <div className="bg-white rounded-xl border border-purple-200 p-3 sm:p-4">
                                            <div className="grid gap-2 sm:gap-3">
                                                {minifigs.map(minifig => {
                                                    const requiredTotal = minifig.required_quantity * quantity;
                                                    const owned = minifig.owned_quantity || 0;
                                                    const isComplete = owned >= requiredTotal;
                                                    const isThisUpdating = updatingMinifigs[minifig.fig_num];

                                                    return (
                                                        <div key={minifig.fig_num} className="bg-slate-50 rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors p-2 sm:p-3">
                                                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-lg overflow-hidden shadow-sm border border-slate-200 flex-shrink-0">
                                                                        <img
                                                                            src={minifig.img_url}
                                                                            alt={minifig.name}
                                                                            className="w-full h-full object-contain"
                                                                            onError={e => { e.target.src = '/images/lego_piece_questionmark.png'; }}
                                                                        />
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <h4 className="font-semibold text-slate-800 truncate text-xs sm:text-sm mb-1">{minifig.name}</h4>
                                                                        <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                                                                            <span className={`text-xs px-2 py-0.5 sm:py-1 rounded-full font-medium ${
                                                                                isComplete
                                                                                    ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                                                                                    : 'bg-amber-100 text-amber-700 border border-amber-200'
                                                                            }`}>
                                                                                {owned}/{requiredTotal}
                                                                            </span>
                                                                            {isComplete && (
                                                                                <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-600 text-xs sm:text-sm" />
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center justify-center sm:justify-end gap-1 sm:gap-2">
                                                                    <button
                                                                        className="w-8 h-8 bg-slate-600 hover:bg-slate-700 text-white rounded-md sm:rounded-lg transition-all duration-200 flex items-center justify-center text-xs sm:text-sm font-semibold disabled:bg-slate-300"
                                                                        onClick={() => updateMinifigQuantity(minifig.fig_num, Math.max(0, owned - 1))}
                                                                        disabled={isThisUpdating || owned <= 0}
                                                                    >−</button>
                                                                    <div className="w-10 text-center">
                                                                        {isThisUpdating ? (
                                                                            <FontAwesomeIcon icon={faSync} className="animate-spin text-slate-400 text-xs sm:text-sm" />
                                                                        ) : (
                                                                            <span className="text-sm font-bold text-slate-800">{owned}</span>
                                                                        )}
                                                                    </div>
                                                                    <button
                                                                        className="w-8 h-8 bg-slate-600 hover:bg-slate-700 text-white rounded-md sm:rounded-lg transition-all duration-200 flex items-center justify-center text-xs sm:text-sm font-semibold disabled:bg-slate-300"
                                                                        onClick={() => updateMinifigQuantity(minifig.fig_num, owned + 1)}
                                                                        disabled={isThisUpdating}
                                                                    >+</button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
                                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <FontAwesomeIcon icon={faUserCircle} className="text-purple-600 text-xl" />
                                    </div>
                                    <h4 className="font-semibold text-slate-800 mb-2">No Minifigure Data Available</h4>
                                    <p className="text-slate-600 text-sm max-w-md mx-auto">
                                        This set doesn't have minifigure inventory data available yet.
                                        Minifigure tracking will be enabled when data becomes available.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="bg-slate-50 border-t border-slate-200 p-4 sm:p-6 flex-shrink-0 rounded-b-xl sm:rounded-b-2xl">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                        {/* Left: Remove set (only for collection sets) */}
                        {onRemove && (
                            <div className="flex items-center gap-2 w-full sm:w-auto">
                                {removeConfirmPending ? (
                                    <>
                                        <span className="text-sm font-medium text-red-700 whitespace-nowrap">Remove this set?</span>
                                        <button
                                            className="px-3 py-2 bg-white hover:bg-slate-100 border border-slate-300 text-slate-600 rounded-lg text-sm font-medium transition-colors"
                                            onClick={() => setRemoveConfirmPending(false)}
                                            disabled={isRemoving}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="px-3 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5"
                                            onClick={async () => {
                                                setIsRemoving(true);
                                                await onRemove();
                                                setIsRemoving(false);
                                            }}
                                            disabled={isRemoving}
                                        >
                                            {isRemoving ? (
                                                <FontAwesomeIcon icon={faSync} className="animate-spin" />
                                            ) : (
                                                <FontAwesomeIcon icon={faTrash} />
                                            )}
                                            <span>Yes, Remove</span>
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        className="px-4 py-2.5 bg-white hover:bg-red-50 border border-red-300 text-red-600 hover:text-red-700 rounded-lg sm:rounded-xl text-sm font-medium transition-all duration-200 flex items-center gap-2"
                                        onClick={() => setRemoveConfirmPending(true)}
                                        disabled={isUpdating}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                        <span>Remove Set</span>
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Right: Cancel + Update */}
                        <div className="flex gap-3 w-full sm:w-auto justify-end">
                            <button
                                className="px-4 sm:px-6 py-2.5 sm:py-3 bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 rounded-lg sm:rounded-xl transition-all duration-200 font-medium text-center"
                                onClick={() => { setRemoveConfirmPending(false); onClose(); }}
                            >
                                Cancel
                            </button>
                            <button
                                className="px-4 sm:px-6 py-2.5 sm:py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg sm:rounded-xl transition-all duration-200 font-medium flex items-center justify-center disabled:bg-red-400"
                                onClick={validateAndUpdate}
                                disabled={isUpdating}
                            >
                                {isUpdating ? (
                                    <>
                                        <FontAwesomeIcon icon={faSync} className="mr-2 animate-spin" />
                                        <span>Updating...</span>
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                                        <span>Update Status</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SetStatusModal;
